// Mock de la TGC Loyalty API, fidèle au contrat openapi.yaml (paths, schémas,
// wrappers de listes, codes d'erreur). Installé comme adapter axios sur
// HTTPClientLoyalty quand VITE_LOYALTY_USE_MOCK=true : les vues et src/api/loyalty
// parlent déjà le vrai contrat, il n'y a que le flag à changer pour brancher le backend.
// Persistance : localStorage (clé loyalty_mock_db_v1).

import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { BRANDING_IMAGE_RULES } from "./types";
import type {
  BrandingImageKind,
  LoyaltyErrorCode,
  LoyaltyNotification,
  Program,
  ProgramActivationHistoryEntry,
  ProgramCustomerSummary,
  ProgramReward,
  RedemptionAdminListItem,
  Reward,
  RewardGroup,
  Tier,
  TierPrice,
} from "./types";

// ─── Store ────────────────────────────────────────────────────────────────────

type MockCustomer = ProgramCustomerSummary & { programId: string };
type MockRedemption = RedemptionAdminListItem & { programId: string };

type Db = {
  seededPlaces: string[];
  programs: Program[];
  tiers: Tier[];
  tierPrices: TierPrice[];
  rewards: Reward[];
  programRewards: ProgramReward[];
  rewardGroups: RewardGroup[];
  customers: MockCustomer[];
  redemptions: MockRedemption[];
  notifications: LoyaltyNotification[];
  activationHistory: ProgramActivationHistoryEntry[];
};

const DB_KEY = "loyalty_mock_db_v1";
const EMPTY_DB: Db = {
  seededPlaces: [],
  programs: [],
  tiers: [],
  tierPrices: [],
  rewards: [],
  programRewards: [],
  rewardGroups: [],
  customers: [],
  redemptions: [],
  notifications: [],
  activationHistory: [],
};

function loadDb(): Db {
  try {
    const raw = localStorage.getItem(DB_KEY);
    return raw ? { ...EMPTY_DB, ...(JSON.parse(raw) as Db) } : { ...EMPTY_DB };
  } catch {
    return { ...EMPTY_DB };
  }
}

function saveDb(db: Db) {
  try {
    localStorage.setItem(DB_KEY, JSON.stringify(db));
  } catch (err) {
    // Les images de branding sont stockées en data URL par le mock : le quota (~5 Mo) peut sauter.
    console.warn("[loyalty mock] écriture localStorage refusée (quota ?) — état gardé en mémoire seulement", err);
  }
}

const uid = (prefix: string) => `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const now = () => new Date().toISOString();
const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString();

// ─── Erreurs (même shape que le vrai backend) ────────────────────────────────

class ApiError extends Error {
  constructor(public status: number, public code: LoyaltyErrorCode, message: string) {
    super(message);
  }
}

const notFound = (what: string) => new ApiError(404, "NOT_FOUND", `${what} not found`);

// ─── Seed (2 programmes de démo par place, au premier listPrograms) ──────────

function seedPlace(db: Db, placeId: string) {
  const p1: Program = {
    id: uid("prg"), placeId, name: "Carte Café", type: "stamps", status: "active",
    earnRatePerEur: null, stampsPerVisit: 1, expiryMode: "anniversary", expiryDays: 365,
    anniversaryAnchor: daysAgo(30).slice(0, 10), tierMode: "global", tierPeriodDays: null, tierPeriodAnchor: null,
    tierInactivityDays: null, tierHardReset: false, nearbyMessage: null,
    branding: null, rewardValidityDays: null, placeLabel: "Coffee Place Inc.",
    createdAt: daysAgo(30), updatedAt: daysAgo(2),
  };
  const p2: Program = {
    ...p1, id: uid("prg"), name: "Programme Points", type: "points", status: "inactive",
    earnRatePerEur: 5, stampsPerVisit: null, expiryDays: 180, createdAt: daysAgo(20), updatedAt: daysAgo(20),
  };
  db.programs.push(p1, p2);

  db.tiers.push(
    { id: uid("tier"), programId: p1.id, rank: 1, name: "Niveau Bronze", threshold: 5, color: null, icon: "🥉", memberCount: 5, createdAt: p1.createdAt, updatedAt: p1.createdAt },
    { id: uid("tier"), programId: p1.id, rank: 2, name: "Niveau Or", threshold: 15, color: null, icon: "🥇", memberCount: 2, createdAt: p1.createdAt, updatedAt: p1.createdAt }
  );

  const rewardSeeds: [string, Reward["type"], string | null, string, boolean][] = [
    ["Café offert", "physical", "Produit gratuit à la prochaine visite", "🎁", true],
    ["15 % de réduction", "discount", "Réduction sur votre prochain achat", "🏷️", true],
    ["Bon de 5 €", "promo_code", "Code : CADEAU05", "🎟️", true],
    ["Dessert offert", "physical", "Au choix parmi la carte", "🍰", false],
  ];
  const thresholds = [5, 8, 10, 10];
  rewardSeeds.forEach(([name, type, description, icon, enabled], i) => {
    const reward: Reward = {
      id: uid("rwd"), placeId, name, description, type, value: null, icon, enabled,
      importedGiftId: null, createdAt: p1.createdAt, updatedAt: p1.createdAt,
    };
    db.rewards.push(reward);
    db.programRewards.push({
      id: uid("prw"), programId: p1.id, rewardId: reward.id,
      threshold: thresholds[i], groupId: null, sortOrder: i, createdAt: p1.createdAt,
    });
  });

  const customerSeeds: [string, string, string, number, number][] = [
    ["Sophie", "Martin", "sophie.martin@gmail.com", 12, 0],
    ["Thomas", "Dubois", "thomas.dubois@outlook.com", 8, 1],
    ["Camille", "Leroy", "camille.leroy@gmail.com", 5, 3],
    ["Nicolas", "Bernard", "n.bernard@icloud.com", 10, 7],
    ["Emma", "Petit", "emma.petit@gmail.com", 3, 10],
    ["Lucas", "Moreau", "lucas.moreau@hotmail.fr", 7, 14],
    ["Chloé", "Simon", "chloe.simon@gmail.com", 9, 20],
  ];
  const [bronze, or] = db.tiers.filter((t) => t.programId === p1.id);
  customerSeeds.forEach(([firstName, lastName, email, balance, lastActiveDays]) => {
    const tier = balance >= 15 ? or : balance >= 5 ? bronze : null;
    db.customers.push({
      programId: p1.id, customerId: uid("cus"), firstName, lastName, email,
      balance, lifetimeEarned: balance + 10,
      tier: tier ? { id: tier.id, name: tier.name } : null,
      lastActivityAt: daysAgo(lastActiveDays), enrolledAt: daysAgo(lastActiveDays + 30),
    });
  });

  const firstAttachment = db.programRewards.find((pr) => pr.programId === p1.id);
  for (let i = 0; i < 4; i++) {
    db.redemptions.push({
      programId: p1.id, id: uid("red"), enrollmentId: uid("enr"),
      programRewardId: firstAttachment?.id ?? uid("prw"), rewardGroupId: null,
      pointsCost: 5, status: "redeemed", tokenExpiresAt: daysAgo(i * 3),
      redeemedAt: daysAgo(i * 3), redeemedBy: "mock-user", balanceAfter: 4, createdAt: daysAgo(i * 3),
    });
  }

  db.notifications.push(
    {
      id: uid("ntf"), programId: p1.id, createdBy: "mock-user", kind: "manual",
      title: "Offre spéciale ce week-end", message: "Venez en profiter !", iconUrl: null,
      segment: null, scheduledAt: null, status: "sent", targetCount: 120,
      sentAt: daysAgo(1.2), createdAt: daysAgo(1.2),
    },
    {
      id: uid("ntf"), programId: p1.id, createdBy: "mock-user", kind: "manual",
      title: "Nouveau menu d'été disponible 🌞", message: "Découvrez la carte.", iconUrl: null,
      segment: null, scheduledAt: null, status: "sent", targetCount: 98,
      sentAt: daysAgo(2.1), createdAt: daysAgo(2.1),
    }
  );

  db.seededPlaces.push(placeId);
}

// ─── Router ───────────────────────────────────────────────────────────────────

type Query = Record<string, string>;
type Handler = (ctx: { db: Db; params: Record<string, string>; query: Query; body: any }) => { status?: number; data: unknown };

const routes: { method: string; pattern: RegExp; keys: string[]; handler: Handler }[] = [];

function on(method: string, path: string, handler: Handler) {
  const keys: string[] = [];
  const pattern = new RegExp(
    "^" + path.replace(/:([a-zA-Z]+)/g, (_, k) => { keys.push(k); return "([^/]+)"; }) + "$"
  );
  routes.push({ method, pattern, keys, handler });
}

const getProgramOr404 = (db: Db, programId: string) => {
  const program = db.programs.find((p) => p.id === programId);
  if (!program) throw notFound("Program");
  return program;
};

// ─── Programs ─────────────────────────────────────────────────────────────────

on("GET", "/programs", ({ db, query }) => {
  const placeId = query.placeId;
  if (!placeId) throw new ApiError(422, "UNPROCESSABLE_ENTITY", "placeId is required");
  if (!db.seededPlaces.includes(placeId)) seedPlace(db, placeId);
  const programs = db.programs
    .filter((p) => p.placeId === placeId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return { data: { programs } };
});

on("POST", "/programs", ({ db, body }) => {
  const program: Program = {
    id: uid("prg"), placeId: body.placeId, name: body.name, type: body.type, status: "inactive",
    earnRatePerEur: body.earnRatePerEur ?? null, stampsPerVisit: body.stampsPerVisit ?? null,
    expiryMode: body.expiryMode ?? "none", expiryDays: body.expiryDays ?? null,
    anniversaryAnchor: body.anniversaryAnchor ?? null,
    tierMode: body.tierMode ?? "global", tierPeriodDays: body.tierPeriodDays ?? null,
    tierPeriodAnchor: body.tierPeriodAnchor ?? null, tierInactivityDays: body.tierInactivityDays ?? null,
    tierHardReset: body.tierHardReset ?? false,
    nearbyMessage: body.nearbyMessage ?? null,
    branding: body.branding ?? null, rewardValidityDays: body.rewardValidityDays ?? null,
    placeLabel: body.placeLabel ?? null,
    createdAt: now(), updatedAt: now(),
  };
  db.programs.push(program);
  return { status: 201, data: program };
});

on("GET", "/branding-upload-url", ({ query }) => {
  // hasOwnProperty.call, not `in` and not Object.hasOwn (ES2022, outside this
  // project's lib): `"__proto__" in rules` is true and would pass the guard.
  const rules = Object.prototype.hasOwnProperty.call(BRANDING_IMAGE_RULES, String(query.kind))
    ? BRANDING_IMAGE_RULES[query.kind as BrandingImageKind]
    : undefined;
  if (!query.placeId || !query.contentType || !rules)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "placeId, kind and contentType are required");
  if (!rules.accept.includes(query.contentType))
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", `contentType not allowed for kind ${query.kind}`);
  // L'API signe contentLength dans l'URL présignée : mêmes règles ici.
  const contentLength = Number(query.contentLength);
  if (!query.contentLength || !Number.isInteger(contentLength) || contentLength <= 0)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "contentLength must be a positive integer");
  if (contentLength > rules.maxBytes)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", `${query.kind} exceeds ${rules.maxBytes} bytes`);
  // uploadUrl "mock:" : pas de S3 ici, uploadBrandingImage encode le fichier en data URL.
  return { data: { uploadUrl: "mock://upload", publicUrl: "", expiresIn: 900 } };
});

on("GET", "/programs/:programId", ({ db, params }) => ({ data: getProgramOr404(db, params.programId) }));

on("PATCH", "/programs/:programId", ({ db, params, body }) => {
  const program = getProgramOr404(db, params.programId);
  if ("placeId" in body || "type" in body || "status" in body)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "placeId, type and status are not patchable");
  Object.assign(program, body, { updatedAt: now() });
  return { data: program };
});

on("DELETE", "/programs/:programId", ({ db, params }) => {
  const program = getProgramOr404(db, params.programId);
  if (program.status === "active") throw new ApiError(409, "CONFLICT", "Cannot delete an active program");
  db.programs = db.programs.filter((p) => p.id !== program.id);
  const removedTierIds = db.tiers.filter((t) => t.programId === program.id).map((t) => t.id);
  db.tiers = db.tiers.filter((t) => t.programId !== program.id);
  db.tierPrices = db.tierPrices.filter((tp) => !removedTierIds.includes(tp.tierId));
  db.programRewards = db.programRewards.filter((pr) => pr.programId !== program.id);
  db.rewardGroups = db.rewardGroups.filter((g) => g.programId !== program.id);
  db.customers = db.customers.filter((c) => c.programId !== program.id);
  db.redemptions = db.redemptions.filter((r) => r.programId !== program.id);
  db.notifications = db.notifications.filter((n) => n.programId !== program.id);
  db.activationHistory = db.activationHistory.filter((h) => h.programId !== program.id);
  return { status: 204, data: null };
});

on("POST", "/programs/:programId/activate", ({ db, params }) => {
  const program = getProgramOr404(db, params.programId);
  db.programs.forEach((p) => {
    if (p.placeId === program.placeId && p.id !== program.id && p.status === "active") {
      p.status = "inactive";
      p.updatedAt = now();
      db.activationHistory.push({
        id: uid("ach"), programId: p.id, action: "deactivated", actorUserId: "mock-user", occurredAt: now(),
      });
    }
  });
  program.status = "active";
  program.updatedAt = now();
  db.activationHistory.push({
    id: uid("ach"), programId: program.id, action: "activated", actorUserId: "mock-user", occurredAt: now(),
  });
  return { data: program };
});

on("GET", "/programs/:programId/activation-history", ({ db, params }) => {
  getProgramOr404(db, params.programId);
  const history = db.activationHistory
    .filter((h) => h.programId === params.programId)
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  return { data: { history } };
});

// ─── Tiers ────────────────────────────────────────────────────────────────────

on("GET", "/programs/:programId/tiers", ({ db, params }) => {
  getProgramOr404(db, params.programId);
  const tiers = db.tiers
    .filter((t) => t.programId === params.programId)
    .sort((a, b) => a.rank - b.rank);
  return { data: { tiers } };
});

on("POST", "/programs/:programId/tiers", ({ db, params, body }) => {
  getProgramOr404(db, params.programId);
  const existing = db.tiers.filter((t) => t.programId === params.programId);
  if (body.rank < 1 || body.rank > 5) throw new ApiError(422, "TIER_LIMIT_REACHED", "rank must be 1-5");
  if (existing.length >= 5) throw new ApiError(422, "TIER_LIMIT_REACHED", "Max 5 tiers per program");
  if (existing.some((t) => t.rank === body.rank)) throw new ApiError(409, "CONFLICT", "Rank already taken");
  const tier: Tier = {
    id: uid("tier"), programId: params.programId, rank: body.rank, name: body.name,
    threshold: body.threshold, color: body.color ?? null, icon: body.icon ?? null,
    memberCount: 0, createdAt: now(), updatedAt: now(),
  };
  db.tiers.push(tier);
  return { status: 201, data: tier };
});

on("PATCH", "/programs/:programId/tiers/:tierId", ({ db, params, body }) => {
  const tier = db.tiers.find((t) => t.id === params.tierId && t.programId === params.programId);
  if (!tier) throw notFound("Tier");
  if (!("name" in body) && !("threshold" in body) && !("color" in body) && !("icon" in body))
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "At least one field is required");
  Object.assign(tier, body, { updatedAt: now() });
  return { data: tier };
});

on("DELETE", "/programs/:programId/tiers/:tierId", ({ db, params, query }) => {
  const tier = db.tiers.find((t) => t.id === params.tierId && t.programId === params.programId);
  if (!tier) throw notFound("Tier");
  if (query.strategy !== "upgrade_all" && query.strategy !== "downgrade_all")
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "strategy is required (upgrade_all | downgrade_all)");
  db.tiers = db.tiers.filter((t) => t.id !== tier.id);
  db.tierPrices = db.tierPrices.filter((tp) => tp.tierId !== tier.id);
  return { data: { deleted: tier.id, migratedMembers: tier.memberCount ?? 0 } };
});

// ─── Tier prices (prix pricés par niveau) ─────────────────────────────────────

const getTierOr404 = (db: Db, programId: string, tierId: string) => {
  const tier = db.tiers.find((t) => t.id === tierId && t.programId === programId);
  if (!tier) throw notFound("Tier");
  return tier;
};

on("GET", "/programs/:programId/tiers/:tierId/prices", ({ db, params }) => {
  getTierOr404(db, params.programId, params.tierId);
  const tierPrices = db.tierPrices.filter((tp) => tp.tierId === params.tierId);
  return { data: { tierPrices } };
});

on("POST", "/programs/:programId/tiers/:tierId/prices", ({ db, params, body }) => {
  getTierOr404(db, params.programId, params.tierId);
  const hasReward = body.programRewardId != null;
  const hasGroup = body.rewardGroupId != null;
  if (hasReward === hasGroup || !(body.price >= 0))
    throw new ApiError(
      422, "UNPROCESSABLE_ENTITY",
      "Exactly one of 'programRewardId'/'rewardGroupId' and a non-negative 'price' are required"
    );
  if (hasReward && !db.programRewards.some((pr) => pr.id === body.programRewardId && pr.programId === params.programId))
    throw notFound("Program reward");
  if (hasGroup && !db.rewardGroups.some((g) => g.id === body.rewardGroupId && g.programId === params.programId))
    throw notFound("Reward group");
  const conflict = db.tierPrices.some(
    (tp) => tp.tierId === params.tierId
      && tp.programRewardId === (body.programRewardId ?? null)
      && tp.rewardGroupId === (body.rewardGroupId ?? null)
  );
  if (conflict) throw new ApiError(409, "CONFLICT", "A price already exists for this tier and target");
  const tierPrice: TierPrice = {
    id: uid("tpr"), tierId: params.tierId,
    programRewardId: body.programRewardId ?? null, rewardGroupId: body.rewardGroupId ?? null,
    price: body.price,
  };
  db.tierPrices.push(tierPrice);
  return { status: 201, data: tierPrice };
});

on("PATCH", "/programs/:programId/tiers/:tierId/prices/:tierPriceId", ({ db, params, body }) => {
  getTierOr404(db, params.programId, params.tierId);
  const tierPrice = db.tierPrices.find((tp) => tp.id === params.tierPriceId && tp.tierId === params.tierId);
  if (!tierPrice) throw notFound("Tier price");
  if (!(body.price >= 0)) throw new ApiError(422, "UNPROCESSABLE_ENTITY", "price (>= 0) is required");
  tierPrice.price = body.price;
  return { data: tierPrice };
});

on("DELETE", "/programs/:programId/tiers/:tierId/prices/:tierPriceId", ({ db, params }) => {
  getTierOr404(db, params.programId, params.tierId);
  const tierPrice = db.tierPrices.find((tp) => tp.id === params.tierPriceId && tp.tierId === params.tierId);
  if (!tierPrice) throw notFound("Tier price");
  db.tierPrices = db.tierPrices.filter((tp) => tp.id !== tierPrice.id);
  return { data: { deleted: tierPrice.id } };
});

// ─── Rewards (catalogue place) ────────────────────────────────────────────────

on("GET", "/rewards", ({ db, query }) => {
  if (!query.placeId) throw new ApiError(422, "UNPROCESSABLE_ENTITY", "placeId is required");
  // Comme l'API : chaque article dit quels programmes l'utilisent. Sans ça le
  // sélecteur de catalogue et l'avertissement de suppression seraient muets en
  // mode maquette.
  const rewards = db.rewards
    .filter((r) => r.placeId === query.placeId)
    .map((r) => ({
      ...r,
      usedByPrograms: db.programRewards
        .filter((pr) => pr.rewardId === r.id)
        .map((pr) => ({
          programId: pr.programId,
          programName: db.programs.find((p) => p.id === pr.programId)?.name ?? "",
        })),
    }));
  return { data: { rewards } };
});

on("POST", "/rewards", ({ db, body }) => {
  const reward: Reward = {
    id: uid("rwd"), placeId: body.placeId, name: body.name, type: body.type,
    description: body.description ?? null, value: body.value ?? null, icon: body.icon ?? null,
    enabled: true, importedGiftId: null, createdAt: now(), updatedAt: now(),
  };
  db.rewards.push(reward);
  return { status: 201, data: reward };
});

on("PATCH", "/rewards/:rewardId", ({ db, params, body }) => {
  const reward = db.rewards.find((r) => r.id === params.rewardId);
  if (!reward) throw notFound("Reward");
  Object.assign(reward, body, { updatedAt: now() });
  return { data: reward };
});

on("DELETE", "/rewards/:rewardId", ({ db, params }) => {
  const reward = db.rewards.find((r) => r.id === params.rewardId);
  if (!reward) throw notFound("Reward");
  db.rewards = db.rewards.filter((r) => r.id !== reward.id);
  db.programRewards = db.programRewards.filter((pr) => pr.rewardId !== reward.id);
  return { data: { deleted: reward.id } };
});

// Importe les Gifts gamification de la place en rewards (idempotent, upsert par importedGiftId).
const DEMO_GIFTS: [string, string, string][] = [
  ["gift_cafe", "Café offert (jeu)", "☕️"],
  ["gift_frite", "Petite frite", "🍟"],
  ["gift_burger", "Burger", "🍔"],
  ["gift_boisson", "Boisson", "🥤"],
];

on("POST", "/rewards/import-gifts", ({ db, body }) => {
  const placeId = body?.placeId;
  if (!placeId) throw new ApiError(422, "UNPROCESSABLE_ENTITY", "placeId is required");
  for (const [giftId, name, icon] of DEMO_GIFTS) {
    const existing = db.rewards.find((r) => r.placeId === placeId && r.importedGiftId === giftId);
    if (existing) {
      existing.name = name;
      existing.icon = icon;
      existing.updatedAt = now();
    } else {
      db.rewards.push({
        id: uid("rwd"), placeId, name, description: "Importée depuis la gamification", type: "physical",
        value: null, icon, enabled: true, importedGiftId: giftId, createdAt: now(), updatedAt: now(),
      });
    }
  }
  return { data: { imported: DEMO_GIFTS.length } };
});

// ─── ProgramRewards (attachements) ────────────────────────────────────────────

on("GET", "/programs/:programId/rewards", ({ db, params }) => {
  getProgramOr404(db, params.programId);
  const rewards = db.programRewards
    .filter((pr) => pr.programId === params.programId)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((pr) => ({ ...pr, reward: db.rewards.find((r) => r.id === pr.rewardId)! }))
    .filter((pr) => pr.reward);
  return { data: { rewards } };
});

on("POST", "/programs/:programId/rewards", ({ db, params, body }) => {
  getProgramOr404(db, params.programId);
  if (body.threshold == null && body.groupId == null)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "A threshold and/or a groupId is required");
  if (db.programRewards.some((pr) => pr.programId === params.programId && pr.rewardId === body.rewardId))
    throw new ApiError(409, "CONFLICT", "Reward already attached");
  const attachment: ProgramReward = {
    id: uid("prw"), programId: params.programId, rewardId: body.rewardId,
    threshold: body.threshold ?? null, groupId: body.groupId ?? null,
    sortOrder: body.sortOrder ?? 0, createdAt: now(),
  };
  db.programRewards.push(attachment);
  return { status: 201, data: attachment };
});

on("PATCH", "/programs/:programId/rewards/:rewardId", ({ db, params, body }) => {
  const attachment = db.programRewards.find(
    (pr) => pr.programId === params.programId && pr.rewardId === params.rewardId
  );
  if (!attachment) throw notFound("Attachment");
  const next = { ...attachment, ...body };
  if (next.threshold == null && next.groupId == null)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "The attachment must keep a threshold or a group");
  Object.assign(attachment, body);
  return { data: attachment };
});

on("DELETE", "/programs/:programId/rewards/:rewardId", ({ db, params }) => {
  const attachment = db.programRewards.find(
    (pr) => pr.programId === params.programId && pr.rewardId === params.rewardId
  );
  if (!attachment) throw notFound("Attachment");
  db.programRewards = db.programRewards.filter((pr) => pr.id !== attachment.id);
  return { data: { detached: params.rewardId } };
});

// ─── RewardGroups ─────────────────────────────────────────────────────────────

const withRewardCount = (db: Db, group: RewardGroup): RewardGroup => ({
  ...group,
  rewardCount: db.programRewards.filter((pr) => pr.groupId === group.id).length,
});

on("GET", "/programs/:programId/reward-groups", ({ db, params, query }) => {
  getProgramOr404(db, params.programId);
  const sortBy = (query.sortBy ?? "threshold") as "threshold" | "name" | "rewardCount";
  const order = query.order === "desc" ? -1 : 1;
  const groups = db.rewardGroups
    .filter((g) => g.programId === params.programId)
    .map((g) => withRewardCount(db, g))
    .sort((a, b) => {
      const va = sortBy === "name" ? a.name : sortBy === "rewardCount" ? a.rewardCount ?? 0 : a.threshold;
      const vb = sortBy === "name" ? b.name : sortBy === "rewardCount" ? b.rewardCount ?? 0 : b.threshold;
      return (va < vb ? -1 : va > vb ? 1 : 0) * order;
    });
  return { data: { groups } };
});

on("POST", "/programs/:programId/reward-groups", ({ db, params, body }) => {
  getProgramOr404(db, params.programId);
  if (!(body.threshold > 0)) throw new ApiError(422, "UNPROCESSABLE_ENTITY", "threshold must be > 0");
  const group: RewardGroup = {
    id: uid("rgp"), programId: params.programId, name: body.name, threshold: body.threshold,
    requiredTierId: body.requiredTierId ?? null, sortOrder: body.sortOrder ?? 0,
    copiedFromGroupId: null, createdAt: now(), updatedAt: now(),
  };
  db.rewardGroups.push(group);
  return { status: 201, data: group };
});

on("POST", "/programs/:programId/reward-groups/copy", ({ db, params, body }) => {
  const program = getProgramOr404(db, params.programId);
  const source = db.rewardGroups.find((g) => g.id === body.sourceGroupId);
  if (!source) throw notFound("Source group");
  const sourceProgram = db.programs.find((p) => p.id === source.programId);
  if (!sourceProgram || sourceProgram.placeId !== program.placeId || source.programId === program.id)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "Source group must belong to another program of the same place");
  const copy: RewardGroup = {
    ...source, id: uid("rgp"), programId: program.id, requiredTierId: null,
    copiedFromGroupId: source.id, createdAt: now(), updatedAt: now(),
  };
  db.rewardGroups.push(copy);
  db.programRewards
    .filter((pr) => pr.groupId === source.id)
    .forEach((pr) => {
      db.programRewards.push({
        id: uid("prw"), programId: program.id, rewardId: pr.rewardId,
        threshold: pr.threshold, groupId: copy.id, sortOrder: pr.sortOrder, createdAt: now(),
      });
    });
  return { status: 201, data: copy };
});

on("PATCH", "/programs/:programId/reward-groups/:groupId", ({ db, params, body }) => {
  const group = db.rewardGroups.find((g) => g.id === params.groupId && g.programId === params.programId);
  if (!group) throw notFound("Group");
  Object.assign(group, body, { updatedAt: now() });
  return { data: withRewardCount(db, group) };
});

on("DELETE", "/programs/:programId/reward-groups/:groupId", ({ db, params }) => {
  const group = db.rewardGroups.find((g) => g.id === params.groupId && g.programId === params.programId);
  if (!group) throw notFound("Group");
  db.rewardGroups = db.rewardGroups.filter((g) => g.id !== group.id);
  db.programRewards.forEach((pr) => { if (pr.groupId === group.id) pr.groupId = null; });
  return { data: { deleted: group.id } };
});

on("POST", "/programs/:programId/reward-groups/:groupId/rewards/:rewardId", ({ db, params }) => {
  const group = db.rewardGroups.find((g) => g.id === params.groupId && g.programId === params.programId);
  if (!group) throw notFound("Group");
  let attachment = db.programRewards.find(
    (pr) => pr.programId === params.programId && pr.rewardId === params.rewardId
  );
  if (attachment) {
    attachment.groupId = group.id;
  } else {
    attachment = {
      id: uid("prw"), programId: params.programId, rewardId: params.rewardId,
      threshold: null, groupId: group.id, sortOrder: 0, createdAt: now(),
    };
    db.programRewards.push(attachment);
  }
  return { data: attachment };
});

on("DELETE", "/programs/:programId/reward-groups/:groupId/rewards/:rewardId", ({ db, params }) => {
  const attachment = db.programRewards.find(
    (pr) => pr.programId === params.programId && pr.rewardId === params.rewardId && pr.groupId === params.groupId
  );
  if (!attachment) throw notFound("Attachment");
  if (attachment.threshold == null)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "No direct threshold to fall back on");
  attachment.groupId = null;
  return { data: attachment };
});

// ─── Customers ────────────────────────────────────────────────────────────────

on("GET", "/programs/:programId/customers", ({ db, params, query }) => {
  getProgramOr404(db, params.programId);
  const limit = Math.min(Number(query.limit) || 50, 200);
  const offset = Math.max(Number(query.offset) || 0, 0);
  const all = db.customers
    .filter((c) => c.programId === params.programId)
    .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
  const customers = all.slice(offset, offset + limit).map(({ programId: _programId, ...c }) => c);
  return { data: { customers, total: all.length, limit, offset } };
});

on("GET", "/programs/:programId/customers/:customerId", ({ db, params }) => {
  const customer = db.customers.find(
    (c) => c.programId === params.programId && c.customerId === params.customerId
  );
  if (!customer) throw notFound("Customer");
  // Ledger de démo cohérent avec le solde : quelques achats + un échange, plus récents d'abord.
  const enrollmentId = uid("enr");
  const spent = customer.lifetimeEarned - customer.balance;
  const lastActive = new Date(customer.lastActivityAt).getTime();
  let running = customer.lifetimeEarned;
  const transactions = [];
  if (spent > 0) {
    transactions.push({
      id: uid("txn"), enrollmentId, type: "debit", amount: spent,
      balanceAfter: customer.balance, reason: "reward_redemption", externalRef: null,
      redemptionId: uid("red"), actorUserId: "mock-user", createdAt: new Date(lastActive - 86400000).toISOString(),
    });
  }
  const credits = Math.min(4, Math.max(1, Math.floor(customer.lifetimeEarned / 3)));
  const perCredit = Math.floor(customer.lifetimeEarned / credits) || 1;
  for (let i = 0; i < credits; i++) {
    const amount = i === credits - 1 ? running : perCredit;
    transactions.push({
      id: uid("txn"), enrollmentId, type: "credit", amount,
      balanceAfter: running, reason: "purchase", externalRef: null,
      redemptionId: null, actorUserId: null,
      createdAt: new Date(lastActive - (i + 2) * 5 * 86400000).toISOString(),
    });
    running -= amount;
  }
  return {
    data: {
      customerId: customer.customerId, firstName: customer.firstName, lastName: customer.lastName,
      email: customer.email, balance: customer.balance, lifetimeEarned: customer.lifetimeEarned,
      tierId: customer.tier?.id ?? null, tierSource: "auto", tierValidUntil: null, tierDecay: null,
      redemptionCount: spent > 0 ? 1 : 0, expiringPoints: 0, nextExpiryAt: null,
      transactions,
    },
  };
});

on("POST", "/programs/:programId/customers/:customerId/balance-adjustments", ({ db, params, body }) => {
  const customer = db.customers.find(
    (c) => c.programId === params.programId && c.customerId === params.customerId
  );
  if (!customer) throw notFound("Customer");
  if (!Number.isInteger(body.delta) || body.delta === 0)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "delta must be a non-zero integer");
  customer.balance += body.delta;
  if (body.delta > 0) customer.lifetimeEarned += body.delta;
  customer.lastActivityAt = now();
  return { data: { balance: customer.balance, tierId: customer.tier?.id ?? null } };
});

on("PUT", "/programs/:programId/customers/:customerId/tier", ({ db, params, body }) => {
  const customer = db.customers.find(
    (c) => c.programId === params.programId && c.customerId === params.customerId
  );
  if (!customer) throw notFound("Customer");
  const tier = body.tierId ? db.tiers.find((t) => t.id === body.tierId) : null;
  if (body.tierId && !tier) throw notFound("Tier");
  customer.tier = tier ? { id: tier.id, name: tier.name } : null;
  return { data: { customerId: customer.customerId, tierId: tier?.id ?? null } };
});

// ─── Redemptions ──────────────────────────────────────────────────────────────

on("GET", "/programs/:programId/redemptions", ({ db, params }) => {
  getProgramOr404(db, params.programId);
  const redemptions = db.redemptions
    .filter((r) => r.programId === params.programId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, 50)
    .map(({ programId: _programId, ...r }) => r);
  return { data: { redemptions } };
});

on("GET", "/programs/:programId/stats", ({ db, params, query }) => {
  getProgramOr404(db, params.programId);
  const monthStart = new Date(new Date().toISOString().slice(0, 7) + "-01T00:00:00.000Z");
  const expiringWithinDays = Number(query.expiringWithinDays) || 7;
  const customers = db.customers.filter((c) => c.programId === params.programId);
  return {
    data: {
      customers: customers.length,
      newThisMonth: customers.filter((c) => new Date(c.enrolledAt) >= monthStart).length,
      redemptionsThisMonth: db.redemptions.filter(
        (r) => r.programId === params.programId
          && r.status === "redeemed"
          && r.redeemedAt != null
          && new Date(r.redeemedAt) >= monthStart
      ).length,
      // Le mock ne modélise pas les lots : approximation par solde non nul.
      expiringSoon: customers.filter((c) => c.balance > 0).length,
      activatedAt: getProgramOr404(db, params.programId).status === "active"
        ? getProgramOr404(db, params.programId).updatedAt
        : null,
      expiringWithinDays,
      month: monthStart.toISOString().slice(0, 10),
    },
  };
});

on("GET", "/programs/:programId/redemptions/monthly-count", ({ db, params }) => {
  getProgramOr404(db, params.programId);
  const monthStart = new Date(new Date().toISOString().slice(0, 7) + "-01T00:00:00.000Z");
  const count = db.redemptions.filter(
    (r) => r.programId === params.programId
      && r.status === "redeemed"
      && r.redeemedAt != null
      && new Date(r.redeemedAt) >= monthStart
  ).length;
  return { data: { count, month: monthStart.toISOString().slice(0, 10) } };
});

// ─── Notifications ────────────────────────────────────────────────────────────

const QUOTA_LIMIT = 3;
const isSameUtcDay = (iso: string) => iso.slice(0, 10) === new Date().toISOString().slice(0, 10);

function quotaFor(db: Db, programId: string) {
  const todays = db.notifications.filter(
    (n) => n.programId === programId && isSameUtcDay(n.scheduledAt ?? n.createdAt)
  );
  const used = todays.filter((n) => ["sent", "sending", "partially_sent"].includes(n.status)).length;
  const reserved = todays.filter((n) => n.status === "scheduled").length;
  const resetsAt = new Date(new Date().setUTCHours(24, 0, 0, 0)).toISOString();
  return { limit: QUOTA_LIMIT, used, reserved, remaining: Math.max(0, QUOTA_LIMIT - used - reserved), resetsAt };
}

on("POST", "/programs/:programId/notifications", ({ db, params, body }) => {
  getProgramOr404(db, params.programId);
  const targetsToday = !body.scheduledAt || isSameUtcDay(body.scheduledAt);
  if (targetsToday && quotaFor(db, params.programId).remaining <= 0)
    throw new ApiError(429, "QUOTA_EXCEEDED", "Daily notification quota reached");
  const scheduled = !!body.scheduledAt;
  const notification: LoyaltyNotification = {
    id: uid("ntf"), programId: params.programId, createdBy: "mock-user", kind: "manual",
    title: body.title, message: body.message, iconUrl: body.iconUrl ?? null,
    segment: body.segment ?? null, scheduledAt: body.scheduledAt ?? null,
    status: scheduled ? "scheduled" : "sent",
    targetCount: db.customers.filter((c) => c.programId === params.programId).length,
    sentAt: scheduled ? null : now(), createdAt: now(),
  };
  db.notifications.push(notification);
  return { status: 201, data: notification };
});

on("GET", "/programs/:programId/notifications", ({ db, params }) => {
  getProgramOr404(db, params.programId);
  const notifications = db.notifications
    .filter((n) => n.programId === params.programId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, 50);
  return { data: { notifications } };
});

on("DELETE", "/programs/:programId/notifications/:notificationId", ({ db, params }) => {
  const notification = db.notifications.find(
    (n) => n.id === params.notificationId && n.programId === params.programId
  );
  if (!notification) throw notFound("Notification");
  if (notification.status !== "draft" && notification.status !== "scheduled")
    throw new ApiError(409, "CONFLICT", "Only draft or scheduled notifications can be cancelled");
  notification.status = "cancelled";
  return { data: notification };
});

on("GET", "/programs/:programId/notifications/quota", ({ db, params }) => {
  getProgramOr404(db, params.programId);
  return { data: quotaFor(db, params.programId) };
});

on("POST", "/programs/:programId/notifications/preview", ({ db, params, body }) => {
  const program = getProgramOr404(db, params.programId);
  const segment = body?.segment;
  let matches = db.customers.filter((c) => c.programId === params.programId);
  if (segment?.tierIds?.length) {
    matches = matches.filter((c) => c.tier && segment.tierIds.includes(c.tier.id));
  }
  if (segment?.pointsExpiringWithinDays != null) {
    const horizonMs = Date.now() + segment.pointsExpiringWithinDays * 86400000;
    matches = matches.filter((c) => {
      if (program.expiryMode === "none" || program.expiryDays == null || c.balance <= 0) return false;
      const anchor = program.expiryMode === "anniversary" ? c.enrolledAt : c.lastActivityAt;
      const expiresAt = new Date(anchor).getTime() + program.expiryDays * 86400000;
      return expiresAt <= horizonMs;
    });
  }
  const customers = matches.length;
  return { data: { customers, devices: { apple: Math.floor(customers * 0.6), google: Math.floor(customers * 0.3) } } };
});

// ─── Nearby config ────────────────────────────────────────────────────────────

on("GET", "/programs/:programId/nearby-config", ({ db, params }) => {
  const program = getProgramOr404(db, params.programId);
  return {
    data: {
      enabled: program.nearbyMessage != null,
      message: program.nearbyMessage,
    },
  };
});

on("PUT", "/programs/:programId/nearby-config", ({ db, params, body }) => {
  const program = getProgramOr404(db, params.programId);
  if ("radiusM" in body)
    throw new ApiError(422, "UNPROCESSABLE_ENTITY", "'radiusM' is not configurable");
  if (body.enabled === false) {
    program.nearbyMessage = null;
  } else {
    if (!body.message)
      throw new ApiError(422, "UNPROCESSABLE_ENTITY", "message is required");
    program.nearbyMessage = body.message;
  }
  program.updatedAt = now();
  return {
    data: {
      enabled: program.nearbyMessage != null,
      message: program.nearbyMessage,
    },
  };
});

// ─── Adapter axios ────────────────────────────────────────────────────────────

const DELAY_MS = 120;

function parseBody(config: AxiosRequestConfig): unknown {
  if (config.data == null) return undefined;
  if (typeof config.data === "string") {
    try { return JSON.parse(config.data); } catch { return config.data; }
  }
  return config.data;
}

export function installLoyaltyMock(client: AxiosInstance) {
  console.info("[loyalty] mock API activé (VITE_LOYALTY_USE_MOCK=true) — contrat openapi.yaml, persistance localStorage");
  client.defaults.adapter = async (config): Promise<AxiosResponse> => {
    await new Promise((resolve) => setTimeout(resolve, DELAY_MS));

    const method = (config.method ?? "get").toUpperCase();
    const [path, queryString] = (config.url ?? "").split("?");
    const query: Query = Object.fromEntries(new URLSearchParams(queryString ?? ""));
    for (const [key, value] of Object.entries(config.params ?? {})) {
      if (value != null) query[key] = String(value);
    }

    for (const route of routes) {
      if (route.method !== method) continue;
      const match = route.pattern.exec(path);
      if (!match) continue;
      const params = Object.fromEntries(route.keys.map((k, i) => [k, match[i + 1]]));

      const db = loadDb();
      try {
        const result = route.handler({ db, params, query, body: parseBody(config) });
        saveDb(db);
        return {
          data: result.data, status: result.status ?? 200, statusText: "OK",
          headers: {}, config: config as never,
        };
      } catch (err) {
        if (err instanceof ApiError) {
          saveDb(db);
          return Promise.reject({
            isAxiosError: true, config, message: err.message,
            response: {
              status: err.status, statusText: err.code, headers: {}, config,
              data: { code: err.code, message: err.message, requestId: uid("req") },
            },
          });
        }
        throw err;
      }
    }

    console.warn(`[loyalty mock] Pas de handler pour ${method} ${path}`);
    return Promise.reject({
      isAxiosError: true, config, message: "Not found",
      response: {
        status: 404, statusText: "NOT_FOUND", headers: {}, config,
        data: { code: "NOT_FOUND", message: `No mock handler for ${method} ${path}`, requestId: uid("req") },
      },
    });
  };
}

/** Vide la base mock (utile en dev pour repartir du seed). */
export function resetLoyaltyMock() {
  localStorage.removeItem(DB_KEY);
}
